import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { mensajeDeError } from '@/lib/errores'

/** Cada cuánto se pregunta por el avance mientras la carga corre en segundo plano. */
const MS_SONDEO = 2500

/**
 * Todo el estado del proyecto de migración, fuera de la pantalla.
 *
 * La consola tenía la lógica y el JSX mezclados en un solo fichero, así que cambiar cuándo
 * se recarga algo obligaba a leerlo entero. Aquí queda el "qué pasa" y allí solo el "cómo se
 * ve", que además hace que las cuatro vistas puedan probarse por separado.
 *
 * El sondeo del avance se para solo cuando la carga termina o falla: dejar un `setInterval`
 * vivo contra el api mientras el asesor se va a comer es la forma más tonta de gastar cuota.
 */
export function useMigracion(propuestaId) {
  const [proyecto, setProyecto] = useState(null)
  const [orden, setOrden] = useState([])
  const [especificaciones, setEspecificaciones] = useState(null)
  const [muestra, setMuestra] = useState([])
  const [hojaActiva, setHojaActiva] = useState(null)
  const [progreso, setProgreso] = useState(null)
  const [hallazgos, setHallazgos] = useState(null)
  const [ocupado, setOcupado] = useState(null) // 'subiendo' | 'clasificando' | 'ensayando' | ...
  const [progresoSubida, setProgresoSubida] = useState(null)
  const [impedimento, setImpedimento] = useState(null)
  // ¿La empresa sigue siendo de prueba? Entregada, ya no se carga, deshace ni vacía desde aquí.
  // `true` hasta que el api diga lo contrario: un api anterior no lo manda.
  const [empresaDemo, setEmpresaDemo] = useState(true)
  const timer = useRef(null)

  const fallo = (e, porDefecto) => toast.error(mensajeDeError(e, porDefecto))

  /**
   * Un fallo que deja el asistente INUTILIZABLE no se puede contar con un toast.
   *
   * Medido en staging el 2026-09-07: una cotización sin empresa demo devuelve 400
   * `SUB_NO_ENCONTRADA` en `especificacion` y en `clasificar`. El asesor podía subir archivos,
   * avanzar de paso y llegar a un desplegable de entidad con UNA opción ("No la cargues"),
   * porque sin especificación no hay entidades que ofrecer. El único aviso era un toast que a
   * los cuatro segundos ya no estaba. Esto lo deja escrito en pantalla hasta que se resuelva.
   */
  const anotarImpedimento = useCallback((e, porDefecto) => {
    const code = e?.response?.data?.code || null
    setImpedimento({ code, detalle: mensajeDeError(e, porDefecto) })
  }, [])

  /* ── Ciclo de vida del proyecto ─────────────────────────────────────────── */

  const cargarProyecto = useCallback(async () => {
    try {
      const r = await cd2bPropuestaService.listarProyectosMigracion(propuestaId)
      const p = (r?.data || [])[0] || null
      setProyecto(p)
      return p
    } catch {
      return null
    }
  }, [propuestaId])

  const cargarEspecificacion = useCallback(async () => {
    try {
      const r = await cd2bPropuestaService.especificacionMigracion(propuestaId)
      setEspecificaciones(r?.data?.especificaciones || null)
      if (Array.isArray(r?.data?.orden)) setOrden(r.data.orden)
      if (typeof r?.data?.empresaDemo === 'boolean') setEmpresaDemo(r.data.empresaDemo)
      setImpedimento(null)
    } catch (e) {
      // NO es "mapear a mano": las entidades que se pueden elegir SALEN de aquí. Sin
      // especificación el desplegable queda vacío y no hay migración posible.
      anotarImpedimento(e, 'No se pudo leer qué se puede migrar a esta empresa')
    }
  }, [propuestaId, anotarImpedimento])

  useEffect(() => {
    cargarProyecto()
    cargarEspecificacion()
  }, [cargarProyecto, cargarEspecificacion])

  const crear = useCallback(
    async (datos) => {
      try {
        const r = await cd2bPropuestaService.crearProyectoMigracion(propuestaId, datos)
        setProyecto(r?.data || null)
        return r?.data
      } catch (e) {
        fallo(e, 'No se pudo abrir la migración')
        return null
      }
    },
    [propuestaId]
  )

  /**
   * Mueve el `estado` real en Mongo, no solo el paso visible del stepper.
   *
   * Navegar por el stepper NO se bloquea (mirar no hace daño), pero el fallo deja de ser mudo:
   * antes, una transición rechazada por `TRANSICIONES` dejaba el paso visible y el estado real
   * diciendo cosas distintas, y el asesor volvía al día siguiente a un proyecto que "estaba en
   * Cuadrar" según la pantalla y en `recopilando` según Mongo.
   */
  const sincronizarEstado = useCallback(
    async (estado) => {
      if (!proyecto?.id) return false
      try {
        const r = await cd2bPropuestaService.cambiarEstadoProyecto(propuestaId, proyecto.id, estado)
        if (r?.data) setProyecto(r.data)
        return true
      } catch (e) {
        fallo(e, 'El avance no se pudo guardar — al recargar volverás al paso anterior')
        return false
      }
    },
    [propuestaId, proyecto?.id]
  )

  const pausar = useCallback(async () => {
    if (!proyecto?.id) return null
    try {
      const r = await cd2bPropuestaService.pausarProyecto(propuestaId, proyecto.id)
      if (r?.data) setProyecto(r.data)
      return r?.data || null
    } catch (e) {
      fallo(e, 'No se pudo guardar la pausa')
      return null
    }
  }, [propuestaId, proyecto?.id])

  /* ── Chat de migración (catálogo por conversación) ─────────────────────────── */

  const [chat, setChat] = useState(null)
  const [catalogoEnVivo, setCatalogoEnVivo] = useState([])
  const [chatHojaId, setChatHojaId] = useState(null)
  const [chatOcupado, setChatOcupado] = useState(false)
  const [chatConfirmando, setChatConfirmando] = useState(false)

  /**
   * Al abrir el modo chat: crea la hoja si hace falta, o recupera la conversación ya iniciada.
   * `entidad` es 'productos' por default (retrocompatible) — si ya hay una hoja de chat
   * activa, el backend la reusa TAL CUAL sin importar qué `entidad` se pida esta vez.
   */
  const chatAbrir = useCallback(
    async (entidad) => {
      let p = proyecto
      if (!p) p = await crear({})
      if (!p) return
      try {
        const r = await cd2bPropuestaService.chatAbrirProyecto(propuestaId, p.id, entidad)
        if (r?.data?.proyecto) setProyecto(r.data.proyecto)
        if (r?.data?.hojaId) setChatHojaId(r.data.hojaId)
        const estado = await cd2bPropuestaService.chatEstadoProyecto(propuestaId, p.id, r?.data?.hojaId)
        setChat(estado?.data?.chat || null)
        setCatalogoEnVivo(estado?.data?.catalogoEnVivo || [])
      } catch (e) {
        fallo(e, 'No se pudo abrir la conversación')
      }
    },
    [propuestaId, proyecto, crear]
  )

  /** La entidad de la hoja de chat activa, derivada de `proyecto.hojas` — nunca duplicada en estado propio. */
  const chatEntidad = useMemo(() => {
    const hoja = (proyecto?.hojas || []).find((h) => h.hojaId === chatHojaId)
    return hoja?.entidad || 'productos'
  }, [proyecto, chatHojaId])

  /**
   * Si el proyecto YA trae una hoja `origen:'chat'` sin cerrar (recarga de página, o se dejó
   * a medias), se recupera la conversación sola al cargar — sin esto, el selector de entidad
   * reaparecería aunque ya hubiera un chat en curso.
   */
  useEffect(() => {
    if (!proyecto?.id || chat || chatHojaId) return
    const hojaChat = (proyecto.hojas || []).find((h) => h.origen === 'chat')
    if (!hojaChat) return
    setChatHojaId(hojaChat.hojaId)
    cd2bPropuestaService
      .chatEstadoProyecto(propuestaId, proyecto.id, hojaChat.hojaId)
      .then((estado) => {
        setChat(estado?.data?.chat || null)
        setCatalogoEnVivo(estado?.data?.catalogoEnVivo || [])
      })
      .catch(() => {
        /* sin recuperación automática el asesor puede reabrir el chat a mano */
      })
  }, [proyecto, chat, chatHojaId, propuestaId])

  const chatEnviarMensaje = useCallback(
    async (mensaje) => {
      if (!proyecto?.id) return
      setChatOcupado(true)
      try {
        const r = await cd2bPropuestaService.chatMensajeProyecto(propuestaId, proyecto.id, mensaje, chatHojaId)
        if (r?.data?.proyecto) setProyecto(r.data.proyecto)
        if (r?.data?.catalogoEnVivo) setCatalogoEnVivo(r.data.catalogoEnVivo)
        if (r?.data?.confirmado) {
          setChat(null)
          setCatalogoEnVivo([])
          toast.success(r.data.respuesta || 'Catálogo confirmado.')
        } else {
          const estado = await cd2bPropuestaService.chatEstadoProyecto(propuestaId, proyecto.id, chatHojaId)
          setChat(estado?.data?.chat || null)
        }
      } catch (e) {
        fallo(e, 'No se pudo enviar el mensaje')
      } finally {
        setChatOcupado(false)
      }
    },
    [propuestaId, proyecto?.id, chatHojaId]
  )

  const chatSubirFoto = useCallback(
    async (file) => {
      if (!proyecto?.id) return
      setChatOcupado(true)
      try {
        const r = await cd2bPropuestaService.chatFotoProyecto(propuestaId, proyecto.id, file, chatHojaId)
        if (r?.data?.proyecto) setProyecto(r.data.proyecto)
        if (r?.data?.catalogoEnVivo) setCatalogoEnVivo(r.data.catalogoEnVivo)
        const estado = await cd2bPropuestaService.chatEstadoProyecto(propuestaId, proyecto.id, chatHojaId)
        setChat(estado?.data?.chat || null)
      } catch (e) {
        fallo(e, 'No se pudo leer la foto')
      } finally {
        setChatOcupado(false)
      }
    },
    [propuestaId, proyecto?.id, chatHojaId]
  )

  /** Recarga chat/catálogo tras un cambio hecho por la sesión de VOZ (backend distinto, mismo estado). */
  const chatRecargarEstado = useCallback(async () => {
    if (!proyecto?.id) return
    try {
      const estado = await cd2bPropuestaService.chatEstadoProyecto(propuestaId, proyecto.id, chatHojaId)
      setChat(estado?.data?.chat || null)
      setCatalogoEnVivo(estado?.data?.catalogoEnVivo || [])
      if (estado?.data?.chat === null) await cargarProyecto() // se confirmó: refresca checklist/estado
    } catch {
      /* la próxima interacción del asesor ya trae el estado fresco */
    }
  }, [propuestaId, proyecto?.id, chatHojaId, cargarProyecto])

  const chatConfirmarCatalogo = useCallback(async () => {
    if (!proyecto?.id) return
    setChatConfirmando(true)
    try {
      const r = await cd2bPropuestaService.chatConfirmarProyecto(propuestaId, proyecto.id, chatHojaId)
      if (r?.data) setProyecto(r.data)
      setChat(null)
      setCatalogoEnVivo([])
      toast.success('Catálogo confirmado.')
    } catch (e) {
      fallo(e, 'No se pudo confirmar el catálogo')
    } finally {
      setChatConfirmando(false)
    }
  }, [propuestaId, proyecto?.id, chatHojaId])

  /* ── Copiloto persistente (acompaña los 4 pasos) ────────────────────────────── */

  const [copiloto, setCopiloto] = useState([])
  const [copilotoPropuestaPendiente, setCopilotoPropuestaPendiente] = useState(null)
  const [copilotoOcupado, setCopilotoOcupado] = useState(false)

  const copilotoCargar = useCallback(async () => {
    if (!proyecto?.id) return
    try {
      const r = await cd2bPropuestaService.copilotoEstadoProyecto(propuestaId, proyecto.id)
      setCopiloto(r?.data?.mensajes || [])
      setCopilotoPropuestaPendiente(r?.data?.propuestaPendiente || null)
    } catch {
      /* el panel arranca vacío; el próximo mensaje del asesor trae el estado fresco igual */
    }
  }, [propuestaId, proyecto?.id])

  useEffect(() => {
    copilotoCargar()
  }, [copilotoCargar])

  // `opcionElegida` es el índice (0-based) de la opción que el asesor eligió con un clic en el
  // banner de propuesta pendiente — cuando viene, `mensaje` puede ir vacío (el backend ejecuta
  // con los parámetros de esa opción, no interpreta texto libre).
  const copilotoEnviarMensaje = useCallback(
    async (mensaje, paso, hojaActivaId, opcionElegida) => {
      if (!proyecto?.id) return
      setCopilotoOcupado(true)
      try {
        const r = await cd2bPropuestaService.copilotoMensajeProyecto(propuestaId, proyecto.id, mensaje, paso, hojaActivaId, opcionElegida)
        if (r?.data?.proyecto) setProyecto(r.data.proyecto)
        await copilotoCargar()
      } catch (e) {
        fallo(e, 'No se pudo enviar el mensaje')
      } finally {
        setCopilotoOcupado(false)
      }
    },
    [propuestaId, proyecto?.id, copilotoCargar]
  )

  const copilotoLimpiar = useCallback(async () => {
    if (!proyecto?.id) return
    try {
      await cd2bPropuestaService.copilotoLimpiarProyecto(propuestaId, proyecto.id)
      await copilotoCargar()
    } catch (e) {
      fallo(e, 'No se pudo limpiar la conversación')
    }
  }, [propuestaId, proyecto?.id, copilotoCargar])

  /* ── Bandeja ────────────────────────────────────────────────────────────── */

  const subirArchivos = useCallback(
    async (archivos) => {
      let p = proyecto
      // Soltar archivos ANTES de crear el proyecto es lo natural: el asesor tiene el USB
      // del cliente en la mano, no viene a "abrir un proyecto".
      if (!p) p = await crear({})
      if (!p) return
      setOcupado('subiendo')
      setProgresoSubida(0)
      try {
        const r = await cd2bPropuestaService.subirArchivosProyecto(propuestaId, p.id, archivos, (ev) => {
          if (ev.total) setProgresoSubida(Math.round((ev.loaded / ev.total) * 100))
        })
        setProyecto(r?.data?.proyecto || null)
        const rechazados = r?.data?.rechazados || []
        if (rechazados.length > 0) toast(`${rechazados.length} archivo(s) no se pudieron leer`, { icon: '⚠️' })
      } catch (e) {
        fallo(e, 'No se pudieron subir los archivos')
      } finally {
        setOcupado(null)
        setProgresoSubida(null)
      }
    },
    [propuestaId, proyecto, crear]
  )

  /** @returns {Promise<boolean>} si de verdad se clasificó — quien llama NO debe avanzar si es `false`. */
  const clasificar = useCallback(async () => {
    if (!proyecto) return false
    setOcupado('clasificando')
    try {
      const r = await cd2bPropuestaService.clasificarHojasProyecto(propuestaId, proyecto.id)
      setProyecto(r?.data?.proyecto || null)
      if (Array.isArray(r?.data?.orden) && r.data.orden.length) setOrden(r.data.orden)
      setImpedimento(null)
      return true
    } catch (e) {
      fallo(e, 'No se pudo reconocer las hojas')
      anotarImpedimento(e, 'No se pudo reconocer las hojas')
      return false
    } finally {
      setOcupado(null)
    }
  }, [propuestaId, proyecto, anotarImpedimento])

  /* ── Preparación ────────────────────────────────────────────────────────── */

  const elegirHoja = useCallback(
    async (hojaId) => {
      setHojaActiva(hojaId)
      setMuestra([])
      if (!proyecto || !hojaId) return
      try {
        const r = await cd2bPropuestaService.detalleHojaProyecto(propuestaId, proyecto.id, hojaId)
        setMuestra(r?.data?.muestra || [])
      } catch {
        /* sin muestra se puede mapear igual, solo que a ciegas */
      }
    },
    [propuestaId, proyecto]
  )

  const ajustarHoja = useCallback(
    async (hojaId, cambios) => {
      if (!proyecto) return
      try {
        await cd2bPropuestaService.ajustarHojaProyecto(propuestaId, proyecto.id, hojaId, cambios)
        await cargarProyecto()
      } catch (e) {
        fallo(e, 'No se pudo guardar el cambio')
      }
    },
    [propuestaId, proyecto, cargarProyecto]
  )

  /**
   * La IA propone el mapeo de una hoja. NO aplica nada: deja una propuesta pendiente que el
   * asesor acepta o descarta. El backend tenía esto entero —endpoint, propuesta y decisión— y
   * la pantalla no lo llamaba desde ningún sitio.
   */
  const sugerirIA = useCallback(
    async (hojaId) => {
      if (!proyecto || !hojaId) return
      setOcupado('sugiriendo')
      try {
        await cd2bPropuestaService.sugerirIAHoja(propuestaId, proyecto.id, hojaId)
        await cargarProyecto()
      } catch (e) {
        fallo(e, 'La IA no pudo proponer un mapeo para esta hoja')
      } finally {
        setOcupado(null)
      }
    },
    [propuestaId, proyecto, cargarProyecto]
  )

  const decidirSugerencia = useCallback(
    async (hojaId, aceptar) => {
      if (!proyecto || !hojaId) return
      try {
        await cd2bPropuestaService.decidirSugerenciaHoja(propuestaId, proyecto.id, hojaId, aceptar)
        await cargarProyecto()
        toast.success(aceptar ? 'Propuesta aplicada.' : 'Propuesta descartada.')
      } catch (e) {
        fallo(e, 'No se pudo registrar la decisión')
      }
    },
    [propuestaId, proyecto, cargarProyecto]
  )

  /* ── Perfiles de mapeo reutilizables ──────────────────────────────────────
   *
   * «El POS Vendex siempre exporta las mismas columnas»: se guarda el mapeo una vez y se
   * reaplica en el siguiente cliente. Existía, pero solo dentro del asistente rápido que
   * estaba plegado — o sea que la pantalla donde de verdad se mapea no lo tenía, y el asesor
   * repetía el mismo mapeo en cada implantación.
   */
  const [perfiles, setPerfiles] = useState([])

  const hoja = (proyecto?.hojas || []).find((h) => h.hojaId === hojaActiva) || null

  const cargarPerfiles = useCallback(async () => {
    if (!hoja?.entidad) return setPerfiles([])
    try {
      const r = await cd2bPropuestaService.listarPerfilesMapeo(hoja.entidad)
      setPerfiles(r?.data || [])
    } catch {
      setPerfiles([]) // sin perfiles se mapea a mano, que es lo de siempre
    }
  }, [hoja?.entidad])

  useEffect(() => {
    cargarPerfiles()
  }, [cargarPerfiles])

  const aplicarPerfil = useCallback(
    async (perfil) => {
      if (!hoja) return
      // Solo las columnas que EXISTEN en este archivo: dos exportaciones del mismo sistema
      // pueden traer columnas de más o de menos.
      const columnas = hoja.encabezados || []
      const aplicable = Object.fromEntries(
        Object.entries(perfil.mapeo || {}).filter(([col]) => columnas.includes(col))
      )
      await ajustarHoja(hoja.hojaId, { mapeo: aplicable })
      const faltan = Object.keys(perfil.mapeo || {}).length - Object.keys(aplicable).length
      toast.success(
        faltan > 0
          ? `Perfil aplicado; ${faltan} columna(s) del perfil no están en este archivo`
          : 'Perfil aplicado'
      )
      try {
        await cd2bPropuestaService.marcarPerfilUsado(perfil._id)
      } catch {
        /* la cuenta de uso es informativa; que falle no rompe nada */
      }
    },
    // `ajustarHoja` se declara más abajo pero existe al invocarse (hoisting de const en el
    // cuerpo del hook): la dependencia se declara igual para que React no avise.
    [hoja] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const guardarPerfil = useCallback(
    async (nombre) => {
      if (!hoja?.entidad || !nombre) return
      try {
        await cd2bPropuestaService.guardarPerfilMapeo({
          nombre,
          entidad: hoja.entidad,
          mapeo: hoja.mapeo || {},
        })
        toast.success('Perfil guardado; lo tendrás en cualquier otro cliente')
        await cargarPerfiles()
      } catch (e) {
        fallo(e, 'No se pudo guardar el perfil')
      }
    },
    [hoja, cargarPerfiles]
  )

  const eliminarPerfil = useCallback(
    async (perfilId) => {
      try {
        await cd2bPropuestaService.eliminarPerfilMapeo(perfilId)
        await cargarPerfiles()
      } catch (e) {
        fallo(e, 'No se pudo borrar el perfil')
      }
    },
    [cargarPerfiles]
  )

  const ensayar = useCallback(
    async (hojaId) => {
      if (!proyecto) return
      setOcupado('ensayando')
      try {
        await cd2bPropuestaService.ensayarHojaProyecto(propuestaId, proyecto.id, hojaId)
        await cargarProyecto()
      } catch (e) {
        fallo(e, 'La prueba falló')
      } finally {
        setOcupado(null)
      }
    },
    [propuestaId, proyecto, cargarProyecto]
  )

  /* ── Cuadre ─────────────────────────────────────────────────────────────── */

  const cuadrar = useCallback(
    async (declarado) => {
      if (!proyecto) return
      setOcupado('cuadrando')
      try {
        const r = await cd2bPropuestaService.cuadrarProyecto(propuestaId, proyecto.id, declarado)
        setProyecto((p) => (p ? { ...p, cuadre: r?.data?.cuadre || null, estado: r?.data?.estado || p.estado } : p))
      } catch (e) {
        fallo(e, 'No se pudo cuadrar')
      } finally {
        setOcupado(null)
      }
    },
    [propuestaId, proyecto]
  )

  /* ── Requerimientos descubiertos ────────────────────────────────────────── */

  const detectarHallazgos = useCallback(
    async (contexto) => {
      if (!proyecto) return
      setOcupado('detectando')
      try {
        const r = await cd2bPropuestaService.detectarHallazgos(propuestaId, proyecto.id, contexto)
        setHallazgos(r?.data || null)
      } catch (e) {
        fallo(e, 'No se pudo revisar qué necesita el cliente')
      } finally {
        setOcupado(null)
      }
    },
    [propuestaId, proyecto]
  )

  const decidirHallazgo = useCallback(
    async (hallazgoId, decision, nota) => {
      if (!proyecto) return
      try {
        const r = await cd2bPropuestaService.decidirHallazgo(propuestaId, proyecto.id, hallazgoId, decision, nota)
        // Se actualiza en sitio: recargar el informe entero volvería a pasar el detector y
        // el asesor perdería el sitio donde estaba leyendo.
        setHallazgos((h) =>
          h ? { ...h, hallazgos: h.hallazgos.map((x) => (x.id === hallazgoId ? r?.data || x : x)) } : h
        )
      } catch (e) {
        fallo(e, 'No se pudo registrar la decisión')
      }
    },
    [propuestaId, proyecto]
  )

  /* ── Carga y avance ─────────────────────────────────────────────────────── */

  const pedirProgreso = useCallback(async () => {
    if (!proyecto) return null
    try {
      const r = await cd2bPropuestaService.progresoProyecto(propuestaId, proyecto.id)
      setProgreso(r?.data?.progreso || null)
      return r?.data || null
    } catch {
      return null
    }
  }, [propuestaId, proyecto])

  const cargar = useCallback(
    async (opts) => {
      if (!proyecto) return
      setOcupado('cargando')
      try {
        await cd2bPropuestaService.cargarProyecto(propuestaId, proyecto.id, opts)
        await pedirProgreso()
      } catch (e) {
        fallo(e, 'No se pudo empezar la carga')
      } finally {
        setOcupado(null)
      }
    },
    [propuestaId, proyecto, pedirProgreso]
  )

  /**
   * Deshacer la carga de UN tipo de dato, desde su tarjeta.
   *
   * Cada tipo de dato entra en su propia carga: corregir el archivo de clientes ya no obliga a
   * borrar el catálogo que entró bien. Dos 409 distintos, y los dos se cuentan con nombre:
   * otra tarjeta cargada después que lo necesita (`dependientes`, el mensaje ya dice cuál), o
   * el cliente que ya operó con esos datos (`bloqueos`: qué dato y por qué).
   */
  const deshacerCarga = useCallback(
    async (entidad, { previsualizar } = {}) => {
      if (!proyecto || !entidad) return null
      try {
        const r = await cd2bPropuestaService.deshacerCargaProyecto(propuestaId, proyecto.id, entidad, { previsualizar })
        if (!previsualizar) {
          toast.success('Se deshizo la carga en la empresa.')
          await cargarProyecto()
        }
        return r?.data || null
      } catch (e) {
        const bloqueos = e?.response?.data?.bloqueos || []
        if (e?.response?.status === 409 && bloqueos.length > 0) {
          const cuales = bloqueos.slice(0, 3).map((b) => `${b.clave} ${b.motivo}`)
          toast.error(
            `No se puede deshacer: el cliente ya usó estos datos (${cuales.join('; ')}${bloqueos.length > 3 ? '…' : ''}).`
          )
        } else {
          fallo(e, 'No se pudo deshacer la carga')
        }
        return null
      }
    },
    [propuestaId, proyecto, cargarProyecto]
  )

  /**
   * Vaciar la empresa de prueba y, con `recargar`, volver a cargar todo el proyecto.
   * Con `previsualizar` solo cuenta: devuelve lo que se borraría, sin tocar nada.
   */
  const vaciarEmpresa = useCallback(
    async ({ previsualizar = false, recargar = false } = {}) => {
      if (!proyecto) return null
      try {
        const r = await cd2bPropuestaService.vaciarEmpresaProyecto(propuestaId, proyecto.id, { previsualizar, recargar })
        if (!previsualizar) {
          toast.success(
            recargar
              ? `Empresa vaciada (${r?.data?.vaciado?.total ?? 0} filas). Cargando todo de nuevo…`
              : 'Empresa vaciada. Todo queda pendiente para volver a cargar.'
          )
          await cargarProyecto()
          if (recargar) await pedirProgreso()
          else setProgreso(null)
        }
        return r?.data || null
      } catch (e) {
        const d = e?.response?.data
        if (d?.code === 'NO_ES_DEMO') setEmpresaDemo(false)
        // Vació pero la carga no arrancó: la empresa ya cambió, así que el proyecto se relee.
        if (d?.vaciado) await cargarProyecto()
        fallo(e, 'No se pudo vaciar la empresa')
        return null
      }
    },
    [propuestaId, proyecto, cargarProyecto, pedirProgreso]
  )

  useEffect(() => {
    const enMarcha = progreso?.fase === 'cargando' || progreso?.fase === 'cerrando'
    if (!enMarcha) {
      if (timer.current) clearInterval(timer.current)
      timer.current = null
      return undefined
    }
    timer.current = setInterval(async () => {
      const d = await pedirProgreso()
      if (d?.progreso?.fase === 'hecho' || d?.progreso?.fase === 'error') await cargarProyecto()
    }, MS_SONDEO)
    return () => {
      if (timer.current) clearInterval(timer.current)
      timer.current = null
    }
  }, [progreso?.fase, pedirProgreso, cargarProyecto])

  return {
    proyecto,
    hoja,
    hojaActiva,
    muestra,
    orden,
    especificaciones,
    progreso,
    ocupado,
    progresoSubida,
    impedimento,
    crear,
    subirArchivos,
    clasificar,
    elegirHoja,
    ajustarHoja,
    ensayar,
    sugerirIA,
    decidirSugerencia,
    perfiles,
    aplicarPerfil,
    guardarPerfil,
    eliminarPerfil,
    cuadrar,
    hallazgos,
    detectarHallazgos,
    decidirHallazgo,
    cargar,
    deshacerCarga,
    vaciarEmpresa,
    empresaDemo,
    recargar: cargarProyecto,
    sincronizarEstado,
    pausar,
    chat,
    catalogoEnVivo,
    chatHojaId,
    chatEntidad,
    chatOcupado,
    chatConfirmando,
    chatAbrir,
    chatEnviarMensaje,
    chatSubirFoto,
    chatConfirmarCatalogo,
    chatRecargarEstado,
    copiloto,
    copilotoPropuestaPendiente,
    copilotoOcupado,
    copilotoEnviarMensaje,
    copilotoLimpiar,
  }
}

export default useMigracion
